import Plotly from "react-plotly.js"
import {Skeleton} from "primereact/skeleton"
import {default as cx} from "classnames"

import MetricCard from "../MetricCard.jsx"
import {fyLabel, useMetricsQuery, metricBadgeProps} from "./util.jsx"

const READINESS_BANDS = [
    {
        key: "lots_of_learning_left",
        header: "Lots of Learning Left: 0-40%",
        background: "bg-light-danger border border-danger",
    },
    {
        key: "making_progress",
        header: "Making Progress: 41-60%",
        background: "bg-light-warning border border-warning",
    },
    {
        key: "almost_done",
        header: "Almost Done: 61-80%",
        background: "bg-light-primary border border-primary",
    },
    {
        key: "ready_for_practice",
        header: "Ready for Practice: 81-100%",
        background: "bg-light-success border border-success",
    },
]

const ReadinessCard = ({header, text, background = "border border-gray-700", isLoading}) => (
    <div className="d-flex flex-fill">
        <div className={cx("card flex-center flex-grow-1 pb-4", background)}>
            <div className="card-header py-0 border-0 text-center flex-fill d-flex flex-center min-h-40px">
                <h3 className="card-title align-items-start flex-column flex-fill flex-center text-center fw-bold fs-4 min-h-30px pt-4">
                    {header}
                </h3>
            </div>
            <div className="card-body d-flex flex-center p-0">
                <div className="d-flex flex-column">
                    {isLoading ? (
                        <Skeleton className="w-100 h-60px" />
                    ) : (
                        <div className="d-flex flex-row justify-content-center align-items-center fw-bolder fs-3x">
                            {text}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
)

const OrientationLengthChart = ({rows, isLoading}) => {
    const levels = rows.map((r) => r.experience_level)

    const traces = [
        {
            x: levels,
            y: rows.map((r) => r.completed_weeks),
            name: "Completed",
            type: "bar",
            marker: {color: "rgba(75,192,192,0.85)"},
            hovertemplate: "%{y:.1f} weeks<extra>Completed</extra>",
        },
    ]

    const layout = {
        barmode: "group",
        font: {
            family: "Inter, sans-serif"
        },
        yaxis: {title: "Weeks", rangemode: "tozero"},
        margin: {l: 50, r: 10, t: 20, b: 50},
        legend: {orientation: "h", x: 0.5, y: 1.15, xanchor: "center", yanchor: "bottom"},
        hovermode: "x unified",
    }

    const config = {displayModeBar: false, responsive: true}

    return (
        <div className="card card-xl-stretch mb-xl-8">
            <div className="card-header border-0 pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bold fs-2 text-gray-900">Average Orientation Length</span>
                    <span className="fs-6 fw-semibold text-gray-700">
                        Average length of orientations completed this fiscal year
                    </span>
                </h3>
            </div>
            <div className="card-body pt-5 h-500px">
                {isLoading ? (
                    <Skeleton className="w-100 h-400px" />
                ) : rows.length === 0 ? (
                    <div className="text-muted text-center py-10">No orientees match the current filters.</div>
                ) : (
                    <Plotly data={traces} layout={layout} config={config} style={{width: "100%"}} />
                )}
            </div>
        </div>
    )
}

const MetricsOverview = ({filters = {}}) => {
    const readinessLoading = false;
    // const {data: readiness, isLoading: readinessLoading} = useMetricsQuery(
    //     "readiness",
    //     filters,
    //     {
    //         total: 123,
    //         bands: {
    //             lotsOfLearningLeft: 30,
    //             makingProgress: 21,
    //             almostDone: 12,
    //             readyForPractice: 2,
    //         },
    //     },
    //     {
    //         withFiscalYear: false,
    //     }
    // )


    const readiness = {
        total: 117,
        lots_of_learning_left: 7,
        making_progress: 26,
        almost_done: 44,
        ready_for_practice: 40,
    };

    // const {
    //     data: overview,
    //     isLoading: overviewLoading,
    //     error,
    // } = useMetricsQuery("overview", filters, {
    //     success_rate: {current: null, prior: null},
    //     percent_complete_at_rfp: {current: null, prior: null},
    //     orientation_length: [],
    // })

    const overviewLoading = false;
    const overview2027 = {
        "success_rate": {
            "current": 93,
            "prior": 89
        },
        "percent_complete_at_rfp": {
            "current": 83,
            "prior": 73
        },
        "orientation_length": [
            {
                "experience_level": "New Graduate",
                "completed_weeks": 10.6
            },
            {
                "experience_level": "New to Specialty",
                "completed_weeks": 9.7
            },
            {
                "experience_level": "With Specialty Experience",
                "completed_weeks": 5.7
            }
        ]
    }

    const overview2026 = {
        "success_rate": {
            "current": 89,
            "prior": 89
        },
        "percent_complete_at_rfp": {
            "current": 73,
            "prior": 75
        },
        "orientation_length": [
            {
                "experience_level": "New Graduate",
                "completed_weeks": 12.0
            },
            {
                "experience_level": "New to Specialty",
                "completed_weeks": 7.5
            },
            {
                "experience_level": "With Specialty Experience",
                "completed_weeks": 4.8
            }
        ]
    }

    const overview2025 = {
        "success_rate": {
            "current": 89,
            "prior": 82
        },
        "percent_complete_at_rfp": {
            "current": 75,
            "prior": 68
        },
        "orientation_length": [
            {
                "experience_level": "New Graduate",
                "completed_weeks": 11.5
            },
            {
                "experience_level": "New to Specialty",
                "completed_weeks": 6.3
            }
        ]
    }

    const overview2024 = {
        "success_rate": {
            "current": 82,
            "prior": 87
        },
        "percent_complete_at_rfp": {
            "current": 68,
            "prior": 0
        },
        "orientation_length": [
            {
                "experience_level": "New Graduate",
                "completed_weeks": 11.3
            },
            {
                "experience_level": "New to Specialty",
                "completed_weeks": 6.8
            }
        ]
    }

    const overview2023 = {
        "success_rate": {
            "current": 87,
            "prior": null
        },
        "percent_complete_at_rfp": {
            "current": 0,
            "prior": null
        },
        "orientation_length": [
            {
                "experience_level": "New Graduate",
                "completed_weeks": 11.1
            },
            {
                "experience_level": "New to Specialty",
                "completed_weeks": 6.6
            }
        ]
    }




    let overview = {};
    if (filters.fiscal_year === 2027) {
        overview = overview2027;
    } else if (filters.fiscal_year === 2026) {
        overview = overview2026;
    } else if (filters.fiscal_year === 2025) {
        overview = overview2025;
    } else if (filters.fiscal_year === 2024) {
        overview = overview2024;
    } else if (filters.fiscal_year === 2023) {
        overview = overview2023;
    }



    // if (error) return <div className="alert alert-danger">Unable to load overview.</div>

    const {success_rate: successRate, percent_complete_at_rfp: rfp, orientation_length: lengths} = overview
    const fyDisplay = fyLabel(filters.fiscal_year)
    const priorDisplay = fyLabel(filters.fiscal_year - 1)

    const successBadge = metricBadgeProps(successRate.current, successRate.prior, true)
    const rfpBadge = metricBadgeProps(rfp.current, rfp.prior, true)

    console.log(readiness.total)
    return (
        <>
            <h2 className="fw-bold mb-2">Active Orientees Scoring RFP</h2>
            <div className="d-flex flex-row gap-8 mb-8">
                <ReadinessCard
                    header="Total Active Orientees"
                    text={readiness.total ?? 0}
                    isLoading={readinessLoading}
                />
                {READINESS_BANDS.map((band) => (
                    <ReadinessCard
                        key={band.key}
                        header={band.header}
                        text={readiness?.[band.key] ?? 0}
                        background={band.background}
                        isLoading={readinessLoading}
                    />
                ))}
            </div>

            <div className="d-flex flex-column flex-lg-row gap-8">
                <MetricCard
                    title="Orientation Success Rate"
                    subtitle={fyDisplay}
                    value={successRate.current ? `${successRate.current}%` : "—"}
                    comparison={successRate.prior ? `${successRate.prior}%: ${priorDisplay}` : null}
                    badge={successBadge}
                    isLoading={overviewLoading}
                />
                <MetricCard
                    title="% Complete @ RFP"
                    subtitle={fyDisplay}
                    value={rfp.current ? `${rfp.current}%` : "—"}
                    comparison={rfp.prior ? `${rfp.prior}%: ${priorDisplay}` : null}
                    badge={rfpBadge}
                    isLoading={overviewLoading}
                />
            </div>

            <div className="row g-5 g-xl-8">
                <div className="col-12">
                    <OrientationLengthChart rows={lengths} isLoading={overviewLoading} />
                </div>
            </div>
        </>
    )
}

export default MetricsOverview
